import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { clearEstablishmentsCache } from '@/lib/establishments';

export type ManagerRole = 'MANAGER' | 'HEAD_MANAGER' | 'SALES_DIRECTOR';

interface ManagerSession {
  id: string;
  email: string;
  name: string;
  code: string;
  commissionBps: number;
  /** Нет у сессий, сохранённых до появления ролей, — до первого /me это обычный менеджер. */
  role?: ManagerRole;
  avatarUrl?: string | null;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  manager: ManagerSession | null;
  setSession: (s: { accessToken: string; refreshToken: string; manager: ManagerSession }) => void;
  setTokens: (access: string, refresh: string) => void;
  /** Свежий профиль с сервера: роль назначает оператор, кабинет подстраивается без перевхода. */
  updateManager: (patch: Partial<ManagerSession>) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      manager: null,
      setSession: (s) => {
        clearEstablishmentsCache();
        set({ accessToken: s.accessToken, refreshToken: s.refreshToken, manager: s.manager });
      },
      setTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),
      updateManager: (patch) =>
        set((s) => (s.manager ? { manager: { ...s.manager, ...patch } } : s)),
      logout: () => {
        clearEstablishmentsCache();
        set({ accessToken: null, refreshToken: null, manager: null });
      },
    }),
    {
      name: 'resstro-manager-auth',
      partialize: (s) => ({
        accessToken: s.accessToken,
        refreshToken: s.refreshToken,
        manager: s.manager,
      }),
    },
  ),
);

/** Права главного: руководитель отдела продаж получает их целиком. */
export function useIsHeadManager(): boolean {
  return useAuthStore((s) => s.manager?.role === 'HEAD_MANAGER' || s.manager?.role === 'SALES_DIRECTOR');
}

/** Оборот компании, сейф, расходы и рейтинг — только руководителю. */
export function useIsSalesDirector(): boolean {
  return useAuthStore((s) => s.manager?.role === 'SALES_DIRECTOR');
}

/** Ждём rehydrate localStorage, иначе Protected видит token=null и вечный loop/blank. */
export function useAuthHydrated(): boolean {
  const [hydrated, setHydrated] = useState(() => useAuthStore.persist.hasHydrated());

  useEffect(() => {
    if (useAuthStore.persist.hasHydrated()) {
      setHydrated(true);
      return;
    }
    const unsub = useAuthStore.persist.onFinishHydration(() => setHydrated(true));
    // страховка: если callback не пришёл (старый zustand / SSR) — не висим
    const t = window.setTimeout(() => setHydrated(true), 300);
    return () => {
      unsub();
      window.clearTimeout(t);
    };
  }, []);

  return hydrated;
}
